import { useEffect, useState, type FormEvent } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Package, Plus, AlertCircle, Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  inventory,
  products,
  type LowStockItem,
  type ProductBatch,
  type UpdateBatchPayload,
  type AdminProduct,
} from '../api/endpoints'
import { Button } from '../components/Button'
import { Card } from '../components/Card'
import { DataTable } from '../components/DataTable'
import { Dialog, DialogContent, DialogClose } from '../components/Dialog'
import { FormField } from '../components/FormField'
import { Input } from '../components/Input'
import { formatDate, formatINR } from '../lib/format'

type Tab = 'all' | 'low' | 'add'

export function InventoryPage() {
  const [tab, setTab] = useState<Tab>('all')
  return (
    <div className="space-y-4 max-w-7xl">
      <div className="flex items-center gap-1 border-b border-line">
        <TabButton active={tab === 'all'} onClick={() => setTab('all')}>
          <Package className="size-4" /> All batches
        </TabButton>
        <TabButton active={tab === 'low'} onClick={() => setTab('low')}>
          <AlertCircle className="size-4" /> Low stock
        </TabButton>
        <TabButton active={tab === 'add'} onClick={() => setTab('add')}>
          <Plus className="size-4" /> Add batch
        </TabButton>
      </div>

      {tab === 'all' ? <AllBatches /> : null}
      {tab === 'low' ? <LowStock /> : null}
      {tab === 'add' ? <AddBatch /> : null}
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-2 ${
        active ? 'border-forest text-forest' : 'border-transparent text-slatey hover:text-ink'
      }`}
    >
      {children}
    </button>
  )
}

function AllBatches() {
  const [data, setData] = useState<ProductBatch[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<ProductBatch | null>(null)

  function reload() {
    setLoading(true)
    inventory
      .batches()
      .then((r) => setData(r.batches))
      .catch((err) => toast.error('Failed to load batches', { description: err.message }))
      .finally(() => setLoading(false))
  }

  useEffect(reload, [])

  async function onDelete(b: ProductBatch) {
    if (!confirm(`Delete batch "${b.batchNumber}"? This cannot be undone.`)) return
    try {
      await inventory.deleteBatch(b.id)
      toast.success('Batch deleted')
      reload()
    } catch (err) {
      toast.error('Delete failed', { description: (err as Error).message })
    }
  }

  const columns: ColumnDef<ProductBatch>[] = [
    { header: 'Batch', accessorKey: 'batchNumber', cell: ({ getValue }) => <span className="font-mono text-xs">{String(getValue())}</span> },
    { header: 'Product', accessorKey: 'productId' },
    {
      header: 'Remaining',
      accessorKey: 'remainingQuantity',
      cell: ({ row }) => (
        <span className={row.original.remainingQuantity <= 5 ? 'text-danger font-medium' : ''}>
          {row.original.remainingQuantity} {row.original.unitType}
        </span>
      ),
    },
    { header: 'Supplier', accessorKey: 'supplierName', cell: ({ getValue }) => getValue() ?? '—' },
    { header: 'Purchase date', accessorKey: 'purchaseDate', cell: ({ getValue }) => formatDate(String(getValue())) },
    { header: 'Expiry', accessorKey: 'expiryDate', cell: ({ getValue }) => formatDate(String(getValue())) },
    {
      header: 'Cost / unit',
      accessorKey: 'purchasePrice',
      cell: ({ row }) => (
        <span className="text-xs">
          {formatINR(row.original.purchasePrice)} / {row.original.quantityPerUnit} units
        </span>
      ),
    },
    {
      header: 'Actions',
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 justify-end">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setEditing(row.original)}
            title="Edit batch"
          >
            <Pencil className="size-4 text-ink" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(row.original)}
            title="Delete batch"
          >
            <Trash2 className="size-4 text-danger" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <>
      <DataTable
        data={data}
        columns={columns}
        loading={loading}
        emptyTitle="No inventory yet"
        emptyDescription="Add your first batch to start tracking stock levels."
      />
      {editing ? (
        <EditBatchDialog
          batch={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            reload()
          }}
        />
      ) : null}
    </>
  )
}

/** Edit-batch dialog. Pre-fills from the row, sends only the fields
 *  the user actually touched (we track an "originals" snapshot to
 *  diff). Server rejects remainingQuantity edits when allocations
 *  exist — that error is shown verbatim in the toast. */
function EditBatchDialog({
  batch,
  onClose,
  onSaved,
}: {
  batch: ProductBatch
  onClose: () => void
  onSaved: () => void
}) {
  const [batchNumber, setBatchNumber] = useState(batch.batchNumber)
  const [supplierName, setSupplierName] = useState(batch.supplierName ?? '')
  const [purchaseDate, setPurchaseDate] = useState(batch.purchaseDate)
  const [expiryDate, setExpiryDate] = useState(batch.expiryDate ?? '')
  const [unitType, setUnitType] = useState(batch.unitType)
  // Stored in paise on the wire but the form uses rupees for editing
  // (matches the AddBatch form).
  const [purchasePrice, setPurchasePrice] = useState(
    (batch.purchasePrice / 100).toString(),
  )
  const [quantityPerUnit, setQuantityPerUnit] = useState(
    String(batch.quantityPerUnit),
  )
  const [remainingQuantity, setRemainingQuantity] = useState(
    String(batch.remainingQuantity),
  )
  const [saving, setSaving] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      const payload: UpdateBatchPayload = {}
      // Only include fields that actually changed — keeps the request
      // small and avoids touching server-side fields by accident.
      if (batchNumber !== batch.batchNumber) payload.batchNumber = batchNumber
      if ((supplierName || null) !== (batch.supplierName ?? null)) {
        payload.supplierName = supplierName.trim() || null
      }
      if (purchaseDate !== batch.purchaseDate) payload.purchaseDate = purchaseDate
      if ((expiryDate || null) !== (batch.expiryDate ?? null)) {
        payload.expiryDate = expiryDate || null
      }
      if (unitType !== batch.unitType) payload.unitType = unitType
      const pp = Math.round(Number(purchasePrice) * 100)
      if (pp !== batch.purchasePrice) payload.purchasePrice = pp
      if (Number(quantityPerUnit) !== batch.quantityPerUnit) {
        payload.quantityPerUnit = Number(quantityPerUnit)
      }
      if (Number(remainingQuantity) !== batch.remainingQuantity) {
        payload.remainingQuantity = Number(remainingQuantity)
      }
      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save')
        onClose()
        return
      }
      await inventory.updateBatch(batch.id, payload)
      toast.success('Batch updated')
      onSaved()
    } catch (err) {
      toast.error('Save failed', { description: (err as Error).message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Edit batch ${batch.batchNumber}`}
        description="Update metadata, pricing, or quantities. Edits to remainingQuantity are blocked once units have been sold."
      >
        <form onSubmit={onSubmit} className="grid grid-cols-2 gap-3">
          <FormField label="Batch number" required>
            <Input
              value={batchNumber}
              onChange={(e) => setBatchNumber(e.target.value)}
              required
            />
          </FormField>
          <FormField label="Supplier">
            <Input
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              placeholder="Ayur Source Pvt Ltd"
            />
          </FormField>
          <FormField label="Purchase price (₹ / unit)" required>
            <Input
              type="number"
              min={0}
              step="0.01"
              value={purchasePrice}
              onChange={(e) => setPurchasePrice(e.target.value)}
              required
            />
          </FormField>
          <FormField label="Quantity per unit" required hint="Original total. Changing this does not change remainingQuantity.">
            <Input
              type="number"
              min={1}
              value={quantityPerUnit}
              onChange={(e) => setQuantityPerUnit(e.target.value)}
              required
            />
          </FormField>
          <FormField label="Remaining quantity" hint="Editable only when no units have been allocated to orders.">
            <Input
              type="number"
              min={0}
              value={remainingQuantity}
              onChange={(e) => setRemainingQuantity(e.target.value)}
            />
          </FormField>
          <FormField label="Unit type">
            <Input
              value={unitType}
              onChange={(e) => setUnitType(e.target.value)}
            />
          </FormField>
          <FormField label="Purchase date" required>
            <Input
              type="date"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
              required
            />
          </FormField>
          <FormField label="Expiry date">
            <Input
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
            />
          </FormField>
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <DialogClose asChild>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" loading={saving}>
              Save changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function LowStock() {
  const [data, setData] = useState<LowStockItem[]>([])
  const [loading, setLoading] = useState(true)
  const [threshold, setThreshold] = useState(5)

  function reload(t = threshold) {
    setLoading(true)
    inventory
      .lowStock(t)
      .then((r) => setData(r.items))
      .catch((err) => toast.error('Failed to load low-stock list', { description: err.message }))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    reload(threshold)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const columns: ColumnDef<LowStockItem>[] = [
    { header: 'Product', accessorKey: 'name' },
    { header: 'Slug', accessorKey: 'slug', cell: ({ getValue }) => <span className="font-mono text-xs">{String(getValue())}</span> },
    {
      header: 'Total stock',
      accessorKey: 'totalStock',
      cell: ({ row }) => (
        <span className={row.original.totalStock === 0 ? 'text-danger font-medium' : ''}>
          {row.original.totalStock}
        </span>
      ),
    },
    { header: 'Threshold', accessorKey: 'threshold' },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 max-w-xs">
        <FormField label="Threshold">
          <Input
            type="number"
            min={1}
            value={threshold}
            onChange={(e) => setThreshold(Math.max(1, parseInt(e.target.value, 10) || 5))}
          />
        </FormField>
        <Button variant="outline" size="sm" onClick={() => reload(threshold)} className="mt-5">
          Apply
        </Button>
      </div>
      <DataTable
        data={data}
        columns={columns}
        loading={loading}
        emptyTitle="No low-stock items"
        emptyDescription={`Nothing below ${threshold} units — inventory looks healthy.`}
      />
    </div>
  )
}

function AddBatch() {
  const [productList, setProductList] = useState<AdminProduct[]>([])
  const [productId, setProductId] = useState('')
  const [batchNumber, setBatchNumber] = useState('')
  const [purchasePrice, setPurchasePrice] = useState('')
  const [quantityPerUnit, setQuantityPerUnit] = useState('')
  const [unitType, setUnitType] = useState('jar')
  const [supplierName, setSupplierName] = useState('')
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split('T')[0])
  const [expiryDate, setExpiryDate] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    products.list().then((r) => setProductList(r.products)).catch(() => {})
  }, [])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      await inventory.createBatch({
        productId,
        batchNumber,
        purchasePrice: Math.round(Number(purchasePrice) * 100),
        quantityPerUnit: Number(quantityPerUnit),
        unitType,
        supplierName: supplierName || undefined,
        purchaseDate,
        expiryDate: expiryDate || undefined,
      })
      toast.success('Batch added')
      // Reset
      setProductId('')
      setBatchNumber('')
      setPurchasePrice('')
      setQuantityPerUnit('')
      setSupplierName('')
      setExpiryDate('')
    } catch (err) {
      toast.error('Failed to add batch', { description: (err as Error).message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card className="max-w-xl p-6">
      <h2 className="font-display text-base font-semibold text-ink">Add stock batch</h2>
      <p className="text-xs text-slatey mt-1">Record a new purchase. Stock becomes available immediately.</p>
      <form onSubmit={onSubmit} className="mt-5 grid grid-cols-2 gap-3">
        <FormField label="Product" required className="col-span-2">
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            required
            className="w-full px-3 py-2 rounded-md border border-line bg-white text-sm text-ink focus:outline-none focus:ring-2 focus:ring-forest/40"
          >
            <option value="">Select a product…</option>
            {productList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.slug})
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Batch number" required>
          <Input value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} placeholder="B1042" required />
        </FormField>
        <FormField label="Supplier" >
          <Input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} placeholder="Ayur Source Pvt Ltd" />
        </FormField>
        <FormField label="Purchase price (₹ / unit)" required>
          <Input type="number" min={0} step="0.01" value={purchasePrice} onChange={(e) => setPurchasePrice(e.target.value)} required />
        </FormField>
        <FormField label="Quantity per unit" required>
          <Input type="number" min={1} value={quantityPerUnit} onChange={(e) => setQuantityPerUnit(e.target.value)} required />
        </FormField>
        <FormField label="Unit type">
          <Input value={unitType} onChange={(e) => setUnitType(e.target.value)} />
        </FormField>
        <FormField label="Purchase date" required>
          <Input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} required />
        </FormField>
        <FormField label="Expiry date" className="col-span-2">
          <Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
        </FormField>
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <Button type="submit" loading={saving}>Add batch</Button>
        </div>
      </form>
    </Card>
  )
}